import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

/**
 * Radix will not take an empty string as an item value — it reserves "" for "nothing chosen" —
 * so "No category" travels under this token and is turned back into '' on the way out.
 */
const NONE = '__none__'

/**
 * A template's category, picked from the ones already in use — with a button beside it for
 * starting a new one.
 *
 * Category is only a grouping: it is the heading the template palette puts its cards under, and a
 * column on the Templates list. Because the grouping is by exact spelling, free text quietly
 * splits a group in two the first time someone types "Virtualisation" next to "Virtualization"
 * (Andrew, 2026-09-18) — so the existing categories are a list to choose from, and typing one is a
 * deliberate act rather than the default.
 *
 * Built on the application's own Select, not a native <select>: the native one opens the
 * operating system's menu, which looks like nothing else in the product (Andrew, 2026-09-21). And
 * "add a new category" is an icon to the RIGHT of the field rather than an entry inside the list —
 * adding is an action, not a value, and a list whose last "option" is a command is a list you
 * have to read to the end before you can trust it.
 *
 * Typing mode is reversible: the X beside the input puts back whatever was chosen before the +
 * was pressed. A category being typed is not in `options` yet, so the field stays in typing mode
 * until it loses focus empty; and a template whose category no other template uses still shows
 * its own value rather than snapping to the first option.
 *
 * Typing mode is DERIVED, not synchronised. It used to be a state that an effect reset from the
 * value on every change — so in a controlled parent, pressing "add" cleared the value, the parent
 * re-rendered with "", the effect read "" as "not typing", and the text box vanished the instant
 * it appeared, leaving the template on "No category". A test whose onChange never fed the value
 * back could not see it; the tests for this now drive a real controlled parent.
 *
 * `disabled` covers the whole field — list, text box and the button beside them — because a
 * read-only template shows its category in whichever of the two forms it happens to be in.
 */
export function CategorySelect({ value, options, onChange, id, className = '', disabled = false }: {
    value: string
    options: string[]
    onChange: (next: string) => void
    id?: string
    className?: string
    disabled?: boolean
}) {
    const { t } = useTranslation()
    const known = [...new Set(options.map((o) => (o || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b))
    const isKnown = !!value && known.includes(value)
    // Pressed "+": typing a new one, whatever the value currently is — including "" at the start,
    // and including a half-typed name that happens to match an existing category on the way to a
    // longer one ("General" on the way to "Generalised" must not flip to the list mid-word).
    const [adding, setAdding] = useState(false)
    // A value no other template uses (a category since retired) can only be shown as text.
    const typing = adding || (!!value && !isKnown)
    // What was chosen before "+" was pressed, so the X can put it back.
    const before = useRef<string>(value)

    const startNew = () => {
        before.current = value
        setAdding(true)
        onChange('')        // a new category, not a rename of the current one
    }
    const backToList = () => {
        setAdding(false)
        onChange(known.includes(before.current) ? before.current : '')
    }

    const addLabel = t('templates.addCategory', 'Add a new category…')
    const backLabel = t('templates.chooseCategory', 'Choose an existing category')

    return (
        <div className={`flex items-center gap-1 ${className}`}>
            {typing ? (
                <Input
                    id={id}
                    className="h-8 min-w-0 flex-1 text-xs"
                    value={value}
                    maxLength={45}
                    disabled={disabled}
                    autoFocus={!value}
                    placeholder={t('templates.newCategoryPlaceholder', 'New category')}
                    onChange={(e) => onChange(e.target.value)}
                    // Empty on the way out means the new category was abandoned: go back to the
                    // list rather than leave an empty box that looks like a broken field.
                    onBlur={() => { if (!value.trim()) backToList() }}
                />
            ) : (
                <Select
                    value={isKnown ? value : NONE}
                    onValueChange={(v) => onChange(v === NONE ? '' : v)}
                    disabled={disabled}
                >
                    <SelectTrigger id={id} className="h-8 min-w-0 flex-1 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[50vh]">
                        <SelectItem value={NONE} className="text-xs text-muted-foreground">
                            {t('templates.noCategory', 'No category')}
                        </SelectItem>
                        {known.map((c) => (
                            <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
            <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={disabled}
                title={typing ? backLabel : addLabel}
                aria-label={typing ? backLabel : addLabel}
                // Pressing the X must not first blur the input into "abandoned", which would
                // restore the list and then have this click undo it.
                onMouseDown={(e) => e.preventDefault()}
                onClick={typing ? backToList : startNew}
            >
                {typing ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
        </div>
    )
}
